import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Search, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", dob: "" };

export default function Customers() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers", search],
    queryFn: () => api.get(`/admin/customers?search=${encodeURIComponent(search)}`).then((r) => r.data),
  });

  const save = async () => {
    try {
      const body = { ...editing, dob: editing.dob || null };
      if (editing.id) await api.put(`/admin/customers/${editing.id}`, body);
      else await api.post("/admin/customers", body);
      qc.invalidateQueries({ queryKey: ["customers"] });
      setEditing(null);
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete customer?")) return;
    await api.delete(`/admin/customers/${id}`);
    qc.invalidateQueries({ queryKey: ["customers"] });
    toast.success("Deleted");
  };
  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success(`Imported ${data.imported} customers (${data.skipped} skipped)`);
    } catch (e) { toast.error(apiError(e, "Import failed")); }
    finally { setImporting(false); }
  };

  return (
    <div data-testid="admin-customers">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="font-display text-3xl text-white mb-1">Customers</h1><p className="text-white/50 text-sm">{customers.length} customers</p></div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => doImport(e.target.files[0])} data-testid="csv-import-input" />
          <button onClick={() => fileRef.current.click()} disabled={importing} data-testid="import-csv-btn" className="border border-white/20 rounded-full px-5 py-2.5 text-sm text-white/80 hover:border-[#D4AF37] flex items-center gap-2">
            {importing ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Import CSV
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-customer-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2"><Plus size={15} /> Add</button>
        </div>
      </div>

      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={16} />
        <Input placeholder="Search name, email, phone…" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#111] border-white/15 pl-10" data-testid="customer-search" />
      </div>

      <div className="overflow-x-auto border border-white/10 rounded-2xl">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-white/50 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3 font-mono-label">Name</th>
              <th className="text-left px-4 py-3 font-mono-label">Contact</th>
              <th className="text-left px-4 py-3 font-mono-label">Bookings</th>
              <th className="text-left px-4 py-3 font-mono-label">Pending</th>
              <th className="text-left px-4 py-3 font-mono-label">Last</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">Loading…</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No customers. Import your CSV to get started.</td></tr>
            ) : customers.map((c, i) => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`customer-row-${i}`}>
                <td className="px-4 py-3 text-white">{c.first_name} {c.last_name}</td>
                <td className="px-4 py-3 text-white/60"><div>{c.email}</div><div className="text-white/40 text-xs">{c.phone}</div></td>
                <td className="px-4 py-3 text-[#D4AF37]">{c.total_bookings}</td>
                <td className="px-4 py-3 text-white/60">{c.pending_appointments}</td>
                <td className="px-4 py-3 text-white/50 text-xs">{c.last_booking || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setEditing({ ...c, dob: c.dob || "" })} data-testid={`edit-customer-${i}`} className="text-white/50 hover:text-[#D4AF37] p-1"><Pencil size={14} /></button>
                    <button onClick={() => remove(c.id)} data-testid={`delete-customer-${i}`} className="text-white/50 hover:text-red-400 p-1"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111] border-white/15 text-white">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Edit" : "New"} Customer</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="First name" value={editing.first_name} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="customer-first" />
                <Input placeholder="Last name" value={editing.last_name} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="customer-last" />
              </div>
              <Input placeholder="Email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="customer-email" />
              <Input placeholder="Phone" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="bg-[#0a0a0a] border-white/15" />
              <Input type="date" value={editing.dob || ""} onChange={(e) => setEditing({ ...editing, dob: e.target.value })} className="bg-[#0a0a0a] border-white/15 [color-scheme:dark]" />
              <button onClick={save} data-testid="save-customer-btn" className="btn-gold w-full rounded-full py-3 text-sm font-mono-label uppercase">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
